import styled from 'styled-components';
import { useState } from 'react';
import { TextField, Button } from '@mui/material';
import { validatePhone, validateEmail } from '../utils/helpers';
import CircularProgress from '@mui/material/CircularProgress';

const Form = () => {
  const [submitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');

  // Validation states
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Handle input changes with validation
  const handleEmailChange = (value) => {
    setEmail(value);
    const error = validateEmail(value);
    setEmailError(error);
  };

  const handlePhoneChange = (value) => {
    setPhoneNumber(value);
    const error = validatePhone(value);
    setPhoneError(error);
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setMessage('');
    setPhoneNumber('');
    setEmailError('');
    setPhoneError('');
  };

  const submitForm = async (e) => {
    e.preventDefault();

    // Validate all fields before submitting
    const emailValidationError = validateEmail(email);
    const phoneValidationError = validatePhone(phoneNumber);

    setEmailError(emailValidationError);
    setPhoneError(phoneValidationError);

    // If there are validation errors, don't submit
    if (emailValidationError || phoneValidationError) {
      console.log('Form has validation errors');
      return;
    }


    setIsLoading(true);
    try {
      await fetch(
        'https://yik3q4romd.execute-api.us-east-1.amazonaws.com/dev/contact-form',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
          },
          body: JSON.stringify({
            name,
            senderEmail: email,
            receiverEmail: 'speechwithbrandy@gmail.com',
            phoneNumber,
            message,
          }),
        }
      );

      setIsSubmitted(true);
      setIsLoading(false);
      resetForm();
    } catch (error) {
      console.log(error);
    }

    console.log('submitted');
  };

  return (
    <FormContainer onSubmit={submitForm}>
      <NameEmailContainer>
        <StyledInput
          variant='outlined'
          type='text'
          placeholder='Name'
          value={name}
          name='name'
          onChange={(event) => setName(event.target.value)}
          required
          helperText=' '
        />

        <StyledInput
          variant='outlined'
          type='email'
          placeholder='Email'
          value={email}
          name='email'
          onChange={(event) => handleEmailChange(event.target.value)}
          required
          error={!!emailError}
          helperText={emailError}
        />

        <StyledInput
          variant='outlined'
          type='tel'
          placeholder='Phone Number'
          value={phoneNumber}
          name='phone'
          onChange={(event) => handlePhoneChange(event.target.value)}
          required
          error={!!phoneError}
          helperText={phoneError}
        />
      </NameEmailContainer>

            {isLoading && <CircularProgress style={{ marginTop: '1rem' }} />}

      {submitted && (
        <h3
          style={{ padding: '1rem', textAlign: 'center', fontWeight: '100pt' }}
        >
          {'Thank you. Your message has been sent.'}
        </h3>
      )}

      <MessageInput
        variant='outlined'
        multiline
        rows={4}
        placeholder='Message'
        value={message}
        name='message'
        onChange={(event) => setMessage(event.target.value)}
        required
        helperText=' '
      />

      <StyledButton
        className='submit-button'
        variant='contained'
        type='submit'
        disabled={!name || !email || !phoneNumber || !message}
      >
        Submit
      </StyledButton>
    </FormContainer>
  );
};

export default Form;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 90%;
  margin-top: 10px;
  padding: 20px;
  align-items: center;
`;

const NameEmailContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  width: 70%;
  gap: 20px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

// Base input styles
const BaseInput = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px !important;

    fieldset {
      border: 1px solid #b8d4e3 !important;
    }

    &:hover fieldset {
      border: 1px solid #4fc3f7 !important;
    }

    &.Mui-focused fieldset {
      border: 2px solid #29b6f6 !important;
    }

    &.Mui-error fieldset {
      border: 2px solid #dc3545 !important;
    }
  }

  .MuiInputBase-input {
    padding: 12px 14px !important;
    font-family: 'Georgia', serif !important;
  }

  .MuiFormHelperText-root {
    font-family: 'Georgia', serif !important;
    font-size: 12px !important;
    margin-left: 14px !important;
    margin-top: 4px !important;
    margin-bottom: 0 !important;
    line-height: 1.4 !important;
    min-height: 20px !important; /* Reserve space to prevent shifting */
    display: flex !important;
    align-items: flex-start !important;

    &.Mui-error {
      color: #dc3545 !important;
      font-weight: 500 !important;
    }

    /* Success state - you can add this class manually */
    &.success {
      color: #28a745 !important;
      font-weight: 500 !important;
    }

    /* Info/hint state */
    &.info {
      color: #17a2b8 !important;
      font-style: italic !important;
    }
  }
`;

// Input for Name and Email (side by side)
const StyledInput = styled(BaseInput)`
  width: 100% !important;
`;

// Input for Message (full width, multiline)
const MessageInput = styled(BaseInput)`
  width: 70% !important;
  margin-top: 20px !important;
  min-height: 100px !important;
`;

const StyledButton = styled(Button)`
  width: 70% !important;
  background: #ff7849 !important;
  color: white !important;
  cursor: pointer !important;
  margin-top: 20px !important;
  text-transform: none !important;

  &:hover {
    background: #54a399ff !important;
  }

  &:disabled {
    background: #cccccc !important;
    cursor: not-allowed !important;
  }
`;
